import { T, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  CircleNotchIcon,
  EnvelopeIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useEmailRules } from '@/api/rpc/hooks/communications/useEmailRules';
import { summarizeEmailRuleFiltersAsLines } from 'src/lib/workflows/emailRuleEventDisplay';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import type { EmailRuleFilters } from '@erp/rpc';

function summarizeFilters(filters: EmailRuleFilters): string[] {
  return summarizeEmailRuleFiltersAsLines(filters);
}

interface EmailRulePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailRuleIds: string[];
  stepLabel: string;
}

export function EmailRulePreviewDialog({
  open,
  onOpenChange,
  emailRuleIds,
  stepLabel,
}: EmailRulePreviewDialogProps) {
  const gt = useGT();
  const { data, isLoading } = useEmailRules();
  const rules = (data?.rules ?? []).filter((r) => emailRuleIds.includes(r.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
            <DialogTitle>{stepLabel}</DialogTitle>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotchIcon className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rules.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            <T context="error when email rules not found in preview">
              No matching email rules found.
            </T>
          </p>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => {
              const lines = summarizeFilters(rule.filters ?? {});
              return (
                <div key={rule.id} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{rule.ruleName}</span>
                    <Badge
                      variant={rule.enabled ? 'secondary' : 'outline'}
                      className="text-xs"
                    >
                      {rule.enabled ? <T>Enabled</T> : <T>Disabled</T>}
                    </Badge>
                  </div>
                  {lines.length > 0 && (
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {lines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}

            <div className="flex justify-end">
              <Button variant="outline" size="sm" asChild>
                <Link to="/inbound-emails/email-rules">
                  <ArrowSquareOutIcon className="mr-1.5 h-4 w-4" />
                  {gt('Open Email Rules', {
                    context:
                      'button to open the email rules page from workflow preview',
                  })}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
